import { type FieldValues, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { zodResolver } from '@hookform/resolvers/zod';
import { TRPCClientError } from '@trpc/client';
import { Button, Col, Form, Row, Space, theme, Typography } from 'antd';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { PosInput } from 'pos-client/components/shared/Form/PosInput.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type CreateOpeningHoursGroupModel,
  CreateOpeningHoursGroupSchema,
} from 'pos-shared/models/openingHoursGroupSchema.ts';

interface HoursGroupsModalProps {
  isVisible: boolean;
  handleOk: () => Promise<void>;
  handleCancel: () => void;
}

export const NewHoursGroupModal = ({
  isVisible,
  handleOk,
  handleCancel,
}: HoursGroupsModalProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { getAllOpeningHoursGroups } = trpc.useContext();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid: isFormValid },
  } = useForm({
    resolver: zodResolver(CreateOpeningHoursGroupSchema),
    mode: 'all',
    shouldFocusError: true,
  });

  const { showError } = useNotifications();

  const { Text } = Typography;

  const navigate = useNavigate();

  const { mutate: createOpeningHours, isLoading: isCreating } =
    trpc.createOpeningHoursGroup.useMutation({
      onSuccess: async (data) => {
        navigate(`/opening-hours/${data.id}/defaultSchedule`);
        await successMutation();
      },
      onError: (err) => {
        if (
          err instanceof TRPCClientError &&
          err?.data?.problemDetails?.code === 409 &&
          !!err?.data?.problemDetails?.message
        ) {
          showError(err.data.problemDetails.message);
        } else {
          showError(
            t('common.notification.error.message', {
              action: 'create',
              entity: 'opening hours group',
            })
          );
        }
      },
    });

  const cancelModal = () => {
    reset();
    handleCancel();
  };
  const successMutation = async () => {
    await getAllOpeningHoursGroups.invalidate();
    await handleOk();
    reset();
  };
  const handleCreateHours: SubmitHandler<FieldValues> = (data) => {
    createOpeningHours(
      data as Pick<CreateOpeningHoursGroupModel, 'code' | 'groupName'>
    );
  };

  return (
    <PosModal
      header={{ title: t('locations.trading.newHoursGroup'), icon: faClock }}
      open={isVisible}
      onCancel={cancelModal}
      footer={
        <Space>
          <Button onClick={cancelModal}>{t('common.buttons.cancel')}</Button>
          <Button
            type="primary"
            onClick={handleSubmit(handleCreateHours)}
            loading={isCreating}
            disabled={!isFormValid}
          >
            {t('locations.trading.createHoursGroup')}
          </Button>
        </Space>
      }
    >
      <Row>
        <Col flex="auto" style={{ marginBottom: token.marginMD }}>
          <Text>{t('locations.trading.hoursGroupCodeHint')}</Text>
        </Col>
      </Row>

      <Row>
        <Col flex="auto">
          <Form layout="vertical">
            <PosInput
              control={control}
              name="groupName"
              label={t('locations.trading.hoursGroupName')}
            />

            <PosInput
              control={control}
              name="code"
              label={t('locations.trading.hoursGroupCode')}
            />
          </Form>
        </Col>
      </Row>
    </PosModal>
  );
};
